import React from 'react';

/**
 * Handles chunk loading errors that occur when a new version is deployed
 * and the user has stale chunks cached in their browser.
 *
 * Common scenarios:
 * - ChunkLoadError: Failed to fetch dynamically imported module
 * - 404 errors for chunks that no longer exist after deployment
 * - Network errors when loading lazy-loaded routes
 */

const RELOAD_KEY = 'chunk-error-reload-attempted';
const RELOAD_TIMEOUT = 5000; // 5 seconds

/**
 * Checks if an error is a chunk loading error
 */
export function isChunkLoadError(error: Error): boolean {
  // Check for common chunk load error patterns
  const errorMessage = error.message.toLowerCase();
  const errorName = error.name.toLowerCase();

  return (
    // Webpack/Vite chunk load errors
    errorName === 'chunkloaderror' ||
    errorMessage.includes('loading chunk') ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('dynamically imported module') ||
    errorMessage.includes('importing a module script failed') ||
    // Safari, cuando el chunk viejo ya no existe y el rewrite del SPA devuelve
    // el index.html en su lugar (FOLDER-2G).
    errorMessage.includes('is not a valid javascript mime type') ||
    // CSS chunk errors
    errorMessage.includes('loading css chunk') ||
    errorMessage.includes('unable to preload css') ||
    errorMessage.includes('unable to preload') ||
    // Generic import errors that could be chunk-related
    (errorMessage.includes('import') && errorMessage.includes('failed'))
  );
}

/**
 * Reloads the page once to pick up fresh chunks, guarded by sessionStorage so a
 * chunk that is genuinely gone can't put us in a reload loop.
 */
function reloadOnceForChunkError(): void {
  // Check if we've already tried to reload
  const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

  if (hasReloaded) {
    console.error(
      'Already attempted reload for chunk error. Not reloading again.'
    );
    // Clear the flag after timeout to allow future reloads
    setTimeout(() => {
      sessionStorage.removeItem(RELOAD_KEY);
    }, RELOAD_TIMEOUT);
    return;
  }

  // Mark that we're attempting a reload
  sessionStorage.setItem(RELOAD_KEY, Date.now().toString());

  // Reload the page to get fresh chunks
  // Using window.location.reload() with force=true (deprecated but works)
  // Alternative: window.location.href = window.location.href
  window.location.reload();
}

/**
 * Handles chunk loading errors by attempting to reload the page once
 */
export function handleChunkLoadError(error: Error): void {
  if (!isChunkLoadError(error)) {
    return;
  }

  console.warn('Chunk load error detected:', error);

  reloadOnceForChunkError();
}

/**
 * Sets up global error handlers for chunk loading errors
 */
export function setupChunkErrorHandler(): void {
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    if (event.error && isChunkLoadError(event.error)) {
      event.preventDefault(); // Prevent default error logging
      handleChunkLoadError(event.error);
    }
  });

  // Handle unhandled promise rejections (dynamic imports often reject on error)
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error && isChunkLoadError(event.reason)) {
      event.preventDefault(); // Prevent default error logging
      handleChunkLoadError(event.reason);
    }
  });

  // Vite-specific: emitted when a preloaded module/CSS fails to load.
  //
  // Do NOT call `event.preventDefault()` here. Vite's `__vitePreload` helper
  // ends in `baseModule().catch(handlePreloadError)`, and `handlePreloadError`
  // only rethrows while the event is *not* cancelled. Cancelling it makes the
  // whole dynamic import resolve with `undefined`, so consumers that read the
  // module namespace crash instead of seeing the load failure — TanStack
  // Router's `lazyRouteComponent` does `res[exportName ?? 'default']` and threw
  // "undefined is not an object" in production. Letting the error through means
  // the import rejects, the router stores it, and the error boundary renders.
  //
  // The event firing is itself proof that a chunk failed, so reload without
  // gating on the payload message (browsers word it inconsistently, and the
  // payload can be missing entirely).
  window.addEventListener('vite:preloadError', (event) => {
    const payload = event as Event & { payload?: Error };
    console.warn('Chunk preload error detected:', payload.payload);
    reloadOnceForChunkError();
  });

  // Clear reload flag on successful load
  window.addEventListener('load', () => {
    const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
    if (hasReloaded) {
      sessionStorage.removeItem(RELOAD_KEY);
    }
  });
}

/**
 * Wrapper for lazy imports with automatic retry on chunk load error
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentional any: value shape is dynamic/untyped here
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      if (error instanceof Error && isChunkLoadError(error)) {
        handleChunkLoadError(error);
        // Return a dummy component while reload happens
        return {
          default: (() => null) as unknown as T,
        };
      }
      throw error;
    }
  });
}
